import React, { createContext, useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { SESSION_ENDED_EVENT } from "utils/sessionCleanup"

export const TenantContext = createContext(null)

const TENANT_STORAGE_KEY = "tenantId"
const DEFAULT_TENANT = process.env.REACT_APP_DEFAULT_TENANT || null

const TenantProvider = ({ children }) => {
  const [tenantId, setTenantIdState] = useState(
    () => localStorage.getItem(TENANT_STORAGE_KEY) || DEFAULT_TENANT || null
  )
  const [tenantName, setTenantName] = useState(null)
  const [tenantSettings, setTenantSettings] = useState({})

  // Keep localStorage in sync whenever tenantId changes
  useEffect(() => {
    if (tenantId) {
      localStorage.setItem(TENANT_STORAGE_KEY, tenantId)
    } else {
      localStorage.removeItem(TENANT_STORAGE_KEY)
    }
  }, [tenantId])

  useEffect(() => {
    const onSessionEnded = () => {
      setTenantIdState(null)
      setTenantName(null)
      setTenantSettings({})
    }
    window.addEventListener(SESSION_ENDED_EVENT, onSessionEnded)
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onSessionEnded)
  }, [])

  const setTenant = useCallback(({ tenantId: id, tenantName: name, tenantSettings: settings } = {}) => {
    setTenantIdState(id || DEFAULT_TENANT || null)
    setTenantName(name || null)
    setTenantSettings(settings || {})
  }, [])

  const clearTenant = useCallback(() => {
    setTenantIdState(DEFAULT_TENANT || null)
    setTenantName(null)
    setTenantSettings({})
    // Note: localStorage sync is handled by the useEffect above
  }, [])

  const value = useMemo(
    () => ({ tenantId, tenantName, tenantSettings, setTenant, clearTenant }),
    [tenantId, tenantName, tenantSettings, setTenant, clearTenant]
  )

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  )
}

TenantProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default TenantProvider
